import React, { FC } from 'react';
import { trackEvent } from '~/helpers/tracking';
import { Product } from '~/types/products';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import setHtml from '~/helpers/setHtml';
import { Check } from '../../../Icons/fill';
import { InfoCircle } from '../../../Icons/outline';
import Tooltip from '../../../Tooltip';
import LegalAnnotation from '~/components/LegalAnnotation';

interface Props {
  index: number;
  product: Product;
  text: string;
  legal?: string;
  isCopper: boolean;
  is200: boolean;
  is500: boolean;
  is1Gig: boolean;
  is2Gig: boolean;
  is5Gig: boolean;
  is7Gig: boolean;
  finePrint?: string;
  expanded?: boolean;
  displayIcon?: boolean;
  setEeroModal: () => void;
}

interface featureType {
  [key: string]: {
    legalKey?: string;
    hasModal?: boolean;
    customClass?: string;
    children?: string;
  };
}

const FeatureItem: FC<Props> = ({
  index,
  product,
  text,
  legal: legalHash,
  isCopper,
  is200,
  is500,
  is1Gig,
  is2Gig,
  is5Gig,
  is7Gig,
  finePrint,
  setEeroModal,
  expanded,
  displayIcon,
}) => {
  const { legal, quiz } = useSelector((store: ReduxState) => store);
  const { legalMap = {} } = legal || {};
  const handleOnClick = (event) => {
    event.preventDefault();
    setEeroModal();
    trackEvent({
      action: 'displayModal',
      data: {
        detail: {
          product: product?.name,
        },
      },
    });

    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'BUTTON',
        location: 'Product Selection Page',
        position: is500 ? '500m' : 'gig',
        text: 'Eero More Details Modal',
      },
    });
  };

  const speed = {
    internet500: is500,
    internetgig: is1Gig,
    internet2gig: is2Gig,
    internet5gig: is5Gig,
    internet7gig: is7Gig,
    internet200: is200,
  };

  const selectedSpeed = Object.keys(speed).find((key) => speed[key]);

  const featuredType: featureType = {
    eero: {
      legalKey: 'eero',
      hasModal: !isCopper,
    },
    tplink: {
      legalKey: isCopper ? 'download' : 'tpLink',
      hasModal: !isCopper,
    },
    youtubeTV: {
      legalKey: isCopper ? 'youtubeTvCopper' : 'youtubeTv',
    },
    uploadSpeed: {
      legalKey: selectedSpeed,
    },
    download: {
      legalKey: 'download',
      customClass: 'plan-card__feature-install',
    },
    expertFee: {
      children: finePrint && `<small className="plan-card__feature-install__fine-print">${finePrint}</small>`,
      customClass: 'plan-card__feature-install',
    },
    vrc: {
      legalKey: 'vrc',
    },
    techUpgrade: {
      legalKey: 'techUpgrade',
    },
    premiumServices: {
      legalKey: 'premiumServices',
    },
  };

  let content = <span>{text}</span>;
  const featured = featuredType[legalHash];
  const legalKey = featuredType[legalHash]?.legalKey;

  const displayLegal = () => {
    if (!quiz?.results) {
      return <LegalAnnotation legalKey={legalKey} />;
    }

    return (
      <Tooltip
        interactive
        position="top"
        theme="light"
        html={
          <div style={{ width: 240 }}>
            <div className="tooltip-info">
              <span {...setHtml(legalMap?.[legalKey]?.text)} />
            </div>
          </div>
        }
      >
        <InfoCircle />
      </Tooltip>
    );
  };

  if (featured) {
    if (featured.hasModal) {
      content = (
        <button onClick={handleOnClick}>
          {text}
          {displayLegal()}
        </button>
      );
    } else {
      content = (
        <span className={featured?.customClass}>
          <span {...setHtml(text)} />
          {displayLegal()}
        </span>
      );
    }
  }

  const VISIBLE_FEATURE_COUNT = 3;

  return (
    <li
      data-testid="plan-card-feature"
      className={`plan-card__feature${index >= VISIBLE_FEATURE_COUNT ? ' hidden' : ''}${expanded ? ' expanded' : ''}${
        displayIcon ? ' w-icon' : ''
      }`}
    >
      <Check />
      {content}
    </li>
  );
};

FeatureItem.displayName = 'FeatureItem';

export default FeatureItem;
